if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.cachedBoundaryGroups = this.buildBoundaryGroupMap();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });

      const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
      if (activeMedia) {
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ activeMedia.dataset.mediaId }"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.applyVariantMediaGroup(activeMedia.dataset.mediaId);
      }

      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const currentMediaId = event?.detail?.currentElement?.dataset?.mediaId;
      const activeMediaId = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]')?.dataset?.mediaId;
      const targetMediaId =
        this.lastSetActiveMediaId &&
        this.lastSetActiveMediaAt &&
        Date.now() - this.lastSetActiveMediaAt < 900 &&
        activeMediaId === this.lastSetActiveMediaId &&
        currentMediaId !== this.lastSetActiveMediaId
          ? this.lastSetActiveMediaId
          : currentMediaId;

      const thumbnail = targetMediaId
        ? this.elements.thumbnails.querySelector(`[data-target="${ targetMediaId }"]`)
        : null;
      this.setActiveThumbnail(thumbnail);
    }

    setActiveVariantMedia(mediaId) {
      this.setActiveMedia(mediaId, true);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.getViewerMediaItemById(mediaId);
      if (!activeMedia) return;
      const resolvedMediaId = activeMedia.dataset.mediaId;
      this.lastSetActiveMediaId = resolvedMediaId;
      this.lastSetActiveMediaAt = Date.now();

      this.applyVariantMediaGroup(resolvedMediaId);

      this.getViewerMediaItems().forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ resolvedMediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        this.resetSliderPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails && this.mql.matches && this.dataset.desktopLayout !== 'stacked') {
          activeMedia.parentElement.scrollTo({ left: 0 });
        } else if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ resolvedMediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    applyVariantMediaGroup(mediaId) {
      const mediaItems = this.getViewerMediaItems();
      if (mediaItems.length < 2) return;

      // Products need at least two variant-marked media items before this
      // grouping behavior can reliably infer a color boundary. Single-color
      // PDPs like Dover still render one variant-marked featured image, which
      // would otherwise cause unrelated media like uploaded videos to be hidden
      // after load because their preview filenames don't match the active stem.
      const variantMarkerCount = mediaItems.filter((item) =>
        item.classList.contains('product__media-item--variant')
      ).length;
      if (variantMarkerCount < 2) {
        this.clearVariantMediaGroup(mediaItems);
        return;
      }

      const activeItem = this.getViewerMediaItemById(mediaId, mediaItems);
      if (!activeItem) {
        this.clearVariantMediaGroup(mediaItems);
        return;
      }

      const activeMediaKey = this.getMediaGroupKey(activeItem);
      if (!activeMediaKey) {
        this.clearVariantMediaGroup(mediaItems);
        return;
      }

      let groupMediaIds = this.getGroupMediaIdsByKey(activeMediaKey, mediaItems);

      if (groupMediaIds.size < 2) {
        groupMediaIds = this.getVariantBoundaryMediaIds(activeItem);
      } else {
        // Merge with boundary group so the variant's featured image is always included
        const boundaryIds = this.getVariantBoundaryMediaIds(activeItem);
        if (boundaryIds.size >= 2) {
          boundaryIds.forEach(id => groupMediaIds.add(id));
        }
      }

      if (groupMediaIds.size < 2) {
        this.clearVariantMediaGroup(mediaItems);
        return;
      }

      let didChangeVisibility = false;
      mediaItems.forEach((item) => {
        const isInGroup = groupMediaIds.has(item.dataset.mediaId);
        if (this.setElementVisibility(item, isInGroup)) didChangeVisibility = true;
      });

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const shouldShow = groupMediaIds.has(thumbnail.dataset.target);
          if (this.setElementVisibility(thumbnail, shouldShow)) didChangeVisibility = true;
        });
      }

      if (didChangeVisibility) this.resetSliderPages();
    }

    getMediaGroupKey(item) {
      const image = item.querySelector('img');
      if (!image) return null;
      const source = image.currentSrc || image.src || image.getAttribute('src');
      if (!source) return null;
      const fileName = source.split('?')[0].split('/').pop();
      if (!fileName) return null;
      let stem = fileName.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
      stem = stem.replace(/[-_][0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, '');
      stem = stem.replace(/(?:[-_]\d{1,2})(?:[-_][a-z0-9-]+)?$/i, '');
      stem = stem.replace(/(?:[-_]?\d{1,2})?(?:retouch(?:ed|copy)?|edited?|copy|final)$/i, '');
      stem = stem.replace(/(?:[-_]\d{1,2})+$/i, '');
      stem = stem.replace(/[-_]+$/i, '');
      return stem || null;
    }

    getGroupMediaIdsByKey(activeMediaKey, mediaItems) {
      const groupMediaIds = new Set();
      mediaItems.forEach((item) => {
        if (this.getMediaGroupKey(item) === activeMediaKey) {
          groupMediaIds.add(item.dataset.mediaId);
        }
      });
      return groupMediaIds;
    }

    buildBoundaryGroupMap() {
      const mediaItems = this.getViewerMediaItems();
      if (mediaItems.length < 2) return new Map();
      const markerIndexes = [];
      mediaItems.forEach((item, index) => {
        if (item.classList.contains('product__media-item--variant')) markerIndexes.push(index);
      });
      if (markerIndexes.length < 2) return new Map();
      const groupMap = new Map();
      for (let i = 0; i < markerIndexes.length; i++) {
        const startIndex = markerIndexes[i];
        const endIndex = i + 1 < markerIndexes.length ? markerIndexes[i + 1] : mediaItems.length;
        const groupIds = new Set(mediaItems.slice(startIndex, endIndex).map(item => item.dataset.mediaId));
        groupIds.forEach(id => groupMap.set(id, groupIds));
      }
      return groupMap;
    }

    getVariantBoundaryMediaIds(activeItem) {
      const mediaId = activeItem.dataset.mediaId;
      return this.cachedBoundaryGroups.get(mediaId) || new Set();
    }

    setElementVisibility(element, shouldShow) {
      if (!element) return false;
      const isViewerItem = element.classList.contains('product__media-item');
      const isThumbnail = element.hasAttribute('data-target');
      if (!isViewerItem && !isThumbnail) return false;

      const wasForcedHidden = element.getAttribute('data-variant-media-forced-hidden') === 'true';
      let didChange = false;

      if (shouldShow) {
        if (element.hidden) { element.hidden = false; didChange = true; }
        if (wasForcedHidden) {
          element.style.removeProperty('display');
          element.removeAttribute('data-variant-media-forced-hidden');
          didChange = true;
        }
        if (isThumbnail && element.style.getPropertyValue('display') !== 'list-item') {
          element.style.setProperty('display', 'list-item');
          didChange = true;
        }
        return didChange;
      }

      if (!element.hidden) { element.hidden = true; didChange = true; }
      if (!wasForcedHidden || element.style.getPropertyValue('display') !== 'none') {
        element.style.setProperty('display', 'none', 'important');
        element.setAttribute('data-variant-media-forced-hidden', 'true');
        didChange = true;
      }
      return didChange;
    }

    clearVariantMediaGroup(mediaItems = null) {
      const viewerItems = mediaItems || this.getViewerMediaItems();
      let didChangeVisibility = false;
      viewerItems.forEach((item) => {
        if (this.setElementVisibility(item, true)) didChangeVisibility = true;
      });
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          if (this.setElementVisibility(thumbnail, true)) didChangeVisibility = true;
        });
      }
      if (didChangeVisibility) this.resetSliderPages();
    }

    resetSliderPages() {
      if (this.elements.viewer && this.elements.viewer.slider) this.elements.viewer.resetPages();
      if (this.elements.thumbnails && this.elements.thumbnails.slider) this.elements.thumbnails.resetPages();
    }

    getViewerMediaItems() {
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
    }

    getViewerMediaItemById(mediaId, mediaItems = null) {
      const viewerItems = mediaItems || this.getViewerMediaItems();
      const targetId = `${mediaId || ''}`;
      if (!targetId) return null;

      const directMatch = viewerItems.find((item) => item.dataset.mediaId === targetId);
      if (directMatch) return directMatch;

      const targetNumericIdMatch = targetId.match(/(\d+)$/);
      if (!targetNumericIdMatch) return null;
      const targetNumericId = targetNumericIdMatch[1];

      return viewerItems.find((item) => {
        const itemId = item.dataset.mediaId || '';
        const itemNumericIdMatch = itemId.match(/(\d+)$/);
        return itemNumericIdMatch && itemNumericIdMatch[1] === targetNumericId;
      }) || null;
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((element) => element.classList.remove('is-active'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      thumbnail.classList.add('is-active');
      if (!this.elements.thumbnails.slider) return;
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
